import { useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { api } from '../../services/api.js'
import UpgradeInline from '../shared/UpgradeInline.jsx'

export default function GedcomImportModal({ treeId, onClose, onImported }) {
  const inputRef = useRef(null)
  const [file, setFile] = useState(null)
  const [importing, setImporting] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)
  const [tierError, setTierError] = useState(null)
  const [dragOver, setDragOver] = useState(false)

  function handleFile(f) {
    if (!f) return
    if (!f.name.match(/\.(ged|gedcom|txt)$/i)) {
      setError('Please select a .ged or .gedcom file.')
      return
    }
    setFile(f)
    setError(null)
    setResult(null)
  }

  async function doImport() {
    if (!file) return
    setImporting(true)
    setError(null)
    setTierError(null)
    try {
      const formData = new FormData()
      formData.append('gedcom', file)
      const res = await api.upload(`/trees/${treeId}/import/gedcom`, formData)
      setResult(res)
      onImported()
    } catch (err) {
      // TIER_LIMIT fires when a Free user tries to import a file whose
      // person count would push the tree past the 50-person cap — show
      // the inline upgrade CTA instead of the generic error.
      if (err.code === 'TIER_LIMIT') setTierError(err)
      else setError(err.message || 'Import failed')
    } finally {
      setImporting(false)
    }
  }

  // Portal to <body> so the modal escapes the tree/places view's stacking
  // context — that ancestor uses backdrop-filter/transform, which traps a
  // position:fixed child and let the Leaflet map paint over the modal. A
  // z-index bump alone can't fix a trapped context; the portal does.
  return createPortal((
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ maxWidth: '480px' }} onClick={e => e.stopPropagation()} role="dialog">
        <div className="modal__header">
          <div className="modal__title">Import GEDCOM</div>
          <button className="modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="modal__body">
          {!result ? (
            <>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: '1.25rem', lineHeight: '1.5' }}>
                Upload a .ged file exported from any genealogy app (Ancestry, FamilySearch, MyHeritage, etc.). Persons and family relationships will be imported into this tree.
              </p>

              <div
                style={{
                  border: `2px dashed ${dragOver ? 'var(--green)' : 'var(--border)'}`,
                  borderRadius: '8px', padding: '2rem',
                  textAlign: 'center', cursor: 'pointer',
                  background: dragOver ? 'color-mix(in srgb, var(--green) 4%, white)' : 'var(--surface)',
                  transition: 'all 0.15s', marginBottom: '1.25rem',
                }}
                onClick={() => inputRef.current?.click()}
                onDragOver={e => { e.preventDefault(); setDragOver(true) }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files?.[0]) }}
              >
                {file ? (
                  <div>
                    <div style={{ fontSize: '1.25rem', marginBottom: '0.4rem' }}>📄</div>
                    <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{file.name}</div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                      {(file.size / 1024).toFixed(1)} KB
                    </div>
                  </div>
                ) : (
                  <div>
                    <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem', opacity: 0.4 }}>📂</div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                      Click to browse or drag a .ged file here
                    </div>
                  </div>
                )}
              </div>

              <input
                ref={inputRef}
                type="file"
                accept=".ged,.gedcom,.txt"
                style={{ display: 'none' }}
                onChange={e => handleFile(e.target.files?.[0])}
              />

              {tierError && (
                <div style={{ marginBottom: '1rem' }}>
                  <UpgradeInline error={tierError} />
                </div>
              )}
              {error && <div className="form-error" style={{ marginBottom: '1rem' }}>{error}</div>}

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  className="btn btn-primary"
                  onClick={doImport}
                  disabled={!file || importing}
                >
                  {importing ? 'Importing…' : 'Import'}
                </button>
                <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '1rem 0' }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>✓</div>
              <div style={{ fontWeight: 600, fontSize: '1rem', marginBottom: '0.5rem' }}>Import complete</div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: '0.4rem' }}>
                {result.persons_created} added, {result.persons_merged || 0} merged with existing records.
              </div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: '1.5rem' }}>
                {result.relationships_created} relationships added
                {result.relationships_skipped ? `, ${result.relationships_skipped} skipped` : ''}
                {result.unresolved_refs ? `, ${result.unresolved_refs} unresolved references` : ''}.
              </div>
              <button className="btn btn-primary" onClick={onClose}>Done</button>
            </div>
          )}
        </div>
      </div>
    </div>
  ), document.body)
}
